import React from 'react';
import { Box, Stack, UnstyledButton, Text, Flex, Badge } from '@mantine/core';
import { IconChevronDown, IconChevronRight } from '@tabler/icons';
import ChildFiltersDropdown, { ChildFilterItem } from './ChildFiltersDropdown';
import { FilterCategory, ActiveFilter } from '../../types';

export interface FiltersListProps {
  title: string;
  categories: FilterCategory[];
  handleCategorySelect: (categoryId: string, categoryType: 'idea' | 'evidence') => void;
  selectedCategory: string;
  childFilters: ChildFilterItem[];
  categoryIdsWithChildren: Set<string>;
  searchInput: React.ReactNode;
  handleChildFilterSelect: (childFilterId: string) => void;
  middlePanelSearch: string;
  topPadding?: number;
  localActiveFilters?: ActiveFilter[];
}

const FiltersList: React.FC<FiltersListProps> = ({
  title,
  categories,
  handleCategorySelect,
  selectedCategory,
  childFilters,
  categoryIdsWithChildren,
  searchInput,
  handleChildFilterSelect,
  middlePanelSearch,
  topPadding = 0,
  localActiveFilters = [],
}) => {
  // Check if a child filter has an active filter
  const hasActiveFilter = (childFilterId: string): boolean => {
    return localActiveFilters.some((filter) => filter.name === childFilterId);
  };

  // Count children with active filters for the selected category
  const getChildrenWithActiveFiltersCount = (): number => {
    return childFilters.filter((child) => hasActiveFilter(child.id)).length;
  };

  // Check if a category has any active filter (for categories without children like date)
  const isCategoryActive = (categoryId: string): boolean => {
    return localActiveFilters.some((filter) => filter.name === categoryId);
  };

  // Get the appropriate badge count for a category
  const getCategoryBadgeCount = (category: FilterCategory): number => {
    // For categories with children when dropdown is open, count from visible childFilters
    if (categoryIdsWithChildren.has(category.id) && category.id === selectedCategory && childFilters.length > 0) {
      return childFilters.filter((child) => hasActiveFilter(child.id)).length;
    }
    // For categories with children (closed), use filterCount
    if (categoryIdsWithChildren.has(category.id)) {
      return category.filterCount;
    }
    // For categories without children (like date), just check if active
    return isCategoryActive(category.id) ? 1 : 0;
  };

  return (
    <Box pt={topPadding}>
      <Text size="xs" fw={600} c="#9ca3af" mb={10} tt="uppercase" lts={0.5}>
        {title}
      </Text>
      <Stack spacing={4} w="100%">
        {categories.map(
          (category) =>
            !category.disabled && (
              <Box key={category.id} w="100%">
                <UnstyledButton
                  onClick={() => !category.disabled && handleCategorySelect(category.id, category.categoryType)}
                  disabled={category.disabled}
                  className={`filter-category-button ${category.active && 
                    !categoryIdsWithChildren.has(category.id) ?
                    'active' :
                    ''} ${category.disabled 
                    ? 'disabled' :
                    ''}`}
                  sx={{
                    borderRadius: 5,
                    outline: 'none',
                    '&:focus, &:focus-visible': { outline: 'none', boxShadow: 'none' },
                  }}
                >
                  <Flex justify="space-between" w="100%">
                    <Flex align="center" gap={8}>
                      {category.label}
                      {categoryIdsWithChildren.has(category.id) && (
                        <Flex align="center" sx={{ flexShrink: 0 }}>
                          {category.active ? (
                            <IconChevronDown size={13} color="#6c757d" stroke={2} />
                          ) : (
                            <IconChevronRight size={13} color="#6c757d" stroke={2} />
                          )}
                        </Flex>
                      )}
                    </Flex>
                    <Box>
                      {getCategoryBadgeCount(category) > 0 && !(categoryIdsWithChildren.has(category.id) && category.id === selectedCategory) && (
                        categoryIdsWithChildren.has(category.id) ? (
                          <Badge
                            size="xs"
                            variant="filled"
                            radius="xl"
                            sx={{
                              backgroundColor: '#4f46e5',
                              color: '#fff',
                              fontWeight: 600,
                              width: 18,
                              height: 18,
                              padding: 0,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontSize: 11,
                            }}
                          >
                            {getCategoryBadgeCount(category)}
                          </Badge>
                        ) : (
                          <Badge
                            size="xs"
                            variant="filled"
                            radius="xl"
                            sx={{
                              backgroundColor: '#4f46e5',
                              color: '#fff',
                              fontWeight: 600,
                              width: 10,
                              height: 10,
                              padding: 0,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontSize: 11,
                            }}
                          >
                            {/* {getCategoryBadgeCount(category)} */}
                          </Badge>
                        )
                      )}
                    </Box>
                  </Flex>
                </UnstyledButton>
                {category.id === selectedCategory && childFilters.length > 0 && (
                  <ChildFiltersDropdown
                    searchInput={searchInput}
                    childFilters={childFilters}
                    middlePanelSearch={middlePanelSearch}
                    handleChildFilterSelect={handleChildFilterSelect}
                    localActiveFilters={localActiveFilters}
                  />
                )}
              </Box>
            )
        )}
      </Stack>
    </Box>
  );
};

export default FiltersList;
